import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type WtlProjectsResp } from '../lib/api';

export interface PickedProject { uuid: string; name: string; address?: string; project_owner_name?: string; stage?: string; sign_date?: string; }

/**
 * Searchable WTL-project typeahead. Replaces the unusable 1200+ item <select>.
 * Type to filter by name/address; click a row to pick. With allowFreeText, a name
 * with no match can still be used as a plain project label (uuid:'' = not linked).
 */
export function ProjectPicker({
  selected,
  onPick,
  allowFreeText = false,
  placeholder = '搜索项目名 / search project name',
}: {
  selected?: PickedProject | null;
  onPick: (p: PickedProject | null) => void;
  allowFreeText?: boolean;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const projects = useQuery({
    queryKey: ['wtl-projects'],
    queryFn: () => api<WtlProjectsResp>('/wtl/projects'),
    staleTime: 5 * 60_000,
  });

  const list = projects.data?.projects ?? [];
  const matches = useMemo(() => {
    const lc = q.trim().toLowerCase();
    const base = lc
      ? list.filter((p) => (p.name ?? '').toLowerCase().includes(lc) || (p.address ?? '').toLowerCase().includes(lc))
      : list;
    return base.slice(0, 25);
  }, [q, list]);

  // Already chosen → show a chip + clear.
  if (selected) {
    return (
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <span className="pill pill-stage">{selected.name || selected.uuid}</span>
        <button type="button" className="small" onClick={() => { onPick(null); setQ(''); }}>✕ 清除 / clear</button>
      </div>
    );
  }

  // Brain not connected → free-text only (or a hint).
  if (projects.data && projects.data.configured === false) {
    return allowFreeText ? (
      <input
        value={q}
        placeholder="项目名 / project name"
        onChange={(e) => { setQ(e.target.value); onPick(e.target.value.trim() ? { uuid: '', name: e.target.value.trim() } : null); }}
      />
    ) : (
      <span className="small muted">连接采购大脑后可选 / connect the brain to pick</span>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
      />
      {open && (
        <div className="card pop-menu" style={{ position: 'absolute', zIndex: 30, width: '100%', maxHeight: 260, overflow: 'auto', marginTop: 2, padding: 4 }}>
          {projects.isLoading && <div className="small muted" style={{ padding: 6 }}>加载项目… / loading projects…</div>}
          {projects.data?.error && <div className="small" style={{ padding: 6, color: 'var(--bad)' }}>{projects.data.error}</div>}
          {!projects.isLoading && matches.length === 0 && (
            allowFreeText && q.trim()
              ? <div className="small" style={{ padding: 6, cursor: 'pointer' }} onMouseDown={(e) => { e.preventDefault(); onPick({ uuid: '', name: q.trim() }); setOpen(false); }}>+ 用 “{q.trim()}” 作为项目名（不关联大脑）/ use as plain name</div>
              : <div className="small muted" style={{ padding: 6 }}>无匹配 / no matches</div>
          )}
          {matches.map((p) => (
            <div
              key={p.uuid}
              onMouseDown={(e) => { e.preventDefault(); onPick({ uuid: p.uuid, name: p.name, address: p.address, project_owner_name: p.project_owner_name, stage: p.stage, sign_date: p.sign_date }); setOpen(false); setQ(''); }}
              style={{ padding: '5px 7px', cursor: 'pointer', borderRadius: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rule, #eee)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div className="small" style={{ fontWeight: 600 }}>{p.name}</div>
              {p.address && <div className="small muted">{p.address}</div>}
            </div>
          ))}
          {!q.trim() && list.length > 25 && (
            <div className="small muted" style={{ padding: 6 }}>显示前 25 个，共 {list.length} 个 — 输入以搜索 / first 25 of {list.length} — type to search</div>
          )}
        </div>
      )}
    </div>
  );
}
